import React, { useState } from "react";
import HeaderPanel from "../../../components/layout/HeaderPanel";
import FooterPanel from "../../../components/layout/FooterPanel";
import { PrimaryButton } from "@fluentui/react/lib/Button";
import { Icon } from "@fluentui/react/lib/Icon";
import { MessageBar, MessageBarType } from "@fluentui/react/lib/MessageBar";
import { formatMessage, formatMessageDefault } from "../../../utils/helpers/LanguageHelper";
import { useAppDispatch } from "../../../redux/hooks/hooks";
import { navigateBack } from "../../../redux/feature/general/navigation-slice";
import { INFO } from "../../../utils/constants/navigation";
import { TROUBLESHOOTING_TOOLS } from "./toolsRegistry";
import { TroubleshootingTool } from "./troubleshooting-tools.types";
import { getHTMLElementAsButtonProps } from "../../../common/accessibility/HTMLElementAsButtonARIA";

interface NotificationState {
  message: string;
  type: MessageBarType;
  visible: boolean;
}

export const TroubleshootingToolsPage: React.FC = () => {
  const dispatch = useAppDispatch();
  const [expandedToolId, setExpandedToolId] = useState<string | null>(null);
  const [notification, setNotification] = useState<NotificationState>({
    message: "",
    type: MessageBarType.info,
    visible: false,
  });

  const toggleTool = (toolId: string): void => {
    setExpandedToolId(expandedToolId === toolId ? null : toolId);
  };

  const handleToolError = (error: Error): void => {
    setNotification({
      message: error.message,
      type: MessageBarType.error,
      visible: true,
    });

    setTimeout(() => {
      setNotification((prev) => ({ ...prev, visible: false }));
    }, 5000);
  };

  const handleToolSuccess = (message: string): void => {
    setNotification({
      message,
      type: MessageBarType.success,
      visible: true,
    });

    setTimeout(() => {
      setNotification((prev) => ({ ...prev, visible: false }));
    }, 3000);
  };

  const handleBack = (): void => {
    dispatch(navigateBack(INFO));
  };

  const renderToolAccordion = (tool: TroubleshootingTool): React.ReactElement => {
    const isExpanded = expandedToolId === tool.id;

    return (
      <div
        key={tool.id}
        className="card"
        style={{
          margin: "0.5rem",
          backgroundColor: "white",
          transition: "all 0.3s ease",
        }}
      >
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            padding: "1rem",
            cursor: "pointer",
            backgroundColor: isExpanded ? "var(--color-lightblue)" : "transparent",
            borderRadius: "4px",
            transition: "background-color 0.2s ease",
          }}
          {...getHTMLElementAsButtonProps({ onClick: () => toggleTool(tool.id) })}
        >
          <div style={{ display: "flex", alignItems: "center", gap: "0.75rem" }}>
            {tool.icon && <Icon iconName={tool.icon} style={{ fontSize: "20px", color: "var(--color-blue)" }} />}
            <div>
              <div style={{ fontWeight: "600", fontSize: "16px", color: "var(--color-black)" }}>
                {formatMessageDefault(tool.name, tool.name)}
              </div>
              <div style={{ fontSize: "13px", color: "var(--color-grey)", marginTop: "0.25rem" }}>
                {formatMessageDefault(tool.description, tool.description)}
              </div>
            </div>
          </div>
          <Icon
            iconName={isExpanded ? "ChevronUp" : "ChevronDown"}
            style={{
              fontSize: "16px",
              color: "var(--color-blue)",
              transition: "transform 0.2s ease",
            }}
          />
        </div>
        {isExpanded && (
          <div
            style={{
              borderTop: "1px solid var(--color-lightblue)",
              paddingTop: "0.5rem",
              animation: "fadeIn 0.3s ease",
            }}
          >
            {React.createElement(tool.component, { onError: handleToolError, onSuccess: handleToolSuccess })}
          </div>
        )}
      </div>
    );
  };

  return (
    <>
      <HeaderPanel applyPadding enableBackArrow onBackArrowClick={handleBack}>
        {formatMessageDefault("cof.troubleshooting.page.title", "Troubleshooting Tools")}
      </HeaderPanel>

      <div style={{ padding: "0.5rem" }}>
        <div style={{ padding: "0.5rem", marginBottom: "0.5rem" }}>
          <p style={{ margin: 0, color: "var(--color-grey)", fontSize: "14px" }}>
            {formatMessageDefault(
              "cof.troubleshooting.page.description",
              "Select a diagnostic tool below to begin analysis"
            )}
          </p>
        </div>

        {notification.visible && (
          <div style={{ margin: "0.5rem", animation: "fadeIn 0.3s ease" }}>
            <MessageBar
              messageBarType={notification.type}
              isMultiline={false}
              onDismiss={() => setNotification((prev) => ({ ...prev, visible: false }))}
              dismissButtonAriaLabel={formatMessageDefault("cof.troubleshooting.message.dismissAria", "Close")}
            >
              {notification.message}
            </MessageBar>
          </div>
        )}

        <div style={{ marginBottom: "4rem" }}>{TROUBLESHOOTING_TOOLS.map((tool) => renderToolAccordion(tool))}</div>
      </div>

      <FooterPanel>
        <PrimaryButton
          text={formatMessage("cof.base.back")}
          onClick={handleBack}
          iconProps={{ iconName: "Back" }}
          styles={{ root: { float: "left" } }}
        />
      </FooterPanel>
      <style>{`
        @keyframes fadeIn {
          from {
            opacity: 0;
            transform: translateY(-10px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
      `}</style>
    </>
  );
};
